/**
 * ChatContainer.js -- "e-pilot" chat agent, always mounted in the right sidebar.
 *
 * Modes:
 *   - General mode:      RAG-based Q&A over attached engineering files (default).
 *   - CAD mode:          Uses /api/v1/cad-agent/invoke to create/modify AutoCAD drawings.
 *                         Auto-enabled when activeSection is 'cad'/'cadviewer'; can be
 *                         manually toggled via the CAD switch (persists across section changes).
 *   - Orchestrator mode: Shown when activeSection === 'orchestrator' (workflow guidance).
 *
 *
 * cadDrawingCreated event:
 *   When the backend streams a "drawing_created" or "job_complete" event (with a drawing_id),
 *   this component dispatches a CustomEvent('cadDrawingCreated') on `window`.
 *   The CAD Viewer component listens for this event to auto-load the new drawing.
 *
 * Streaming response handling:
 *   sendMessage() uses raw fetch() (not Axios) to stream NDJSON from the backend.
 *   Each line is a JSON object with a `type` field:
 *     "step"            -- intermediate thinking step (appended to msg.thinking[])
 *     "chunk"           -- text chunk (appended to msg.content)
 *     "tool_call"       -- tool invocation in CAD mode
 *     "drawing_created" -- a new CAD drawing was generated
 *     "bom_extracted"   -- BOM extraction result
 *     "job_started"     -- async job ID
 *     "job_complete"    -- async job finished (may include drawing_id + urn)
 *     "model_created"   -- 3D model generation result with download URL
 *     "final"           -- complete response with optional citations
 *     "error"           -- error message
 *   The agent message placeholder is updated in-place via setMessages map.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Switch,
  useTheme
} from '@mui/material';
import { Refresh, Search, AutoAwesome, Architecture, Hub } from '@mui/icons-material';
import client, { getFreshToken } from '../../services/apiClient';
import { api } from '../../services/api';

import useChatFiles from '../../hooks/useChatFiles';
import useChatHistory from '../../hooks/useChatHistory';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import FileAttachmentList from './FileAttachmentList';
import { sanitizeError } from '../../utils/sanitizeError';
import { useNotification } from '../../context/NotificationContext';

function ChatContainer({ sessionId, uploadedFiles: initialFiles = [], context = {}, activeSection = '' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError: showErrorNotif } = useNotification();

  // --- Extracted hooks ---
  const {
    selectedFiles,
    searchQuery,
    setSearchQuery,
    isSearching,
    allFiles,
    displayedFiles,
    fetchFiles,
    handleSearch,
    toggleFileSelection,
  } = useChatFiles({ context, initialFiles });

  const { messages, setMessages } = useChatHistory(sessionId);

  // --- Local state ---
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [cadMode, setCadMode] = useState(false);
  const [mcpMode, setMcpMode] = useState(false);
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const [kbDocCount, setKbDocCount] = useState(0);
  const cadModeManualRef = useRef(false);
  const abortControllerRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Wrapper for manual toggle — marks it as user-initiated
  const handleCadModeToggle = (checked) => {
    cadModeManualRef.current = checked;
    setCadMode(checked);
  };

  // Listen for CAD mode activation from CAD Viewer
  useEffect(() => {
    const handleEnableCadMode = () => {
      cadModeManualRef.current = true;
      setCadMode(true);
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      localStorage.removeItem('epilot_cad_mode');
    };

    if (localStorage.getItem('epilot_cad_mode') === 'true') {
      handleEnableCadMode();
    }

    window.addEventListener('enableCadMode', handleEnableCadMode);
    return () => window.removeEventListener('enableCadMode', handleEnableCadMode);
  }, []);

  // Auto-enable CAD mode when on CAD view, auto-disable when leaving (unless manually toggled)
  useEffect(() => {
    if (activeSection === 'cad' || activeSection === 'cadviewer') {
      setCadMode(true);
    } else if (!cadModeManualRef.current) {
      setCadMode(false);
    }
  }, [activeSection]);

  // Fetch KB doc count when project changes
  useEffect(() => {
    const pid = context?.project_id;
    if (!pid) { setKbDocCount(0); return; }
    api.getProjectKB(pid)
      .then(res => setKbDocCount(res.data?.document_count || 0))
      .catch(() => setKbDocCount(0));
  }, [context?.project_id]);

  // Detect orchestrator mode from activeSection
  const orchestratorMode = activeSection === 'orchestrator';

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('eis_session_id', newSessionId);
    window.dispatchEvent(new CustomEvent('chatSessionChanged', {
      detail: { sessionId: newSessionId }
    }));
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    // CAD mode requires a project — drawings need a project association
    if (cadMode && !context?.project_id) {
      setMessages(prev => [...prev, {
        role: 'agent',
        content: 'Please select or create a project first. CAD drawings need to be associated with a project to appear in your file list.',
        timestamp: new Date().toLocaleTimeString(),
      }]);
      return;
    }

    const currentInput = input;
    const currentSelectedFiles = [...selectedFiles];

    const userMessage = {
      role: 'user',
      content: currentInput,
      timestamp: new Date().toLocaleTimeString(),
      attachedFiles: allFiles.filter(f => currentSelectedFiles.includes(f.file_id))
    };
    setMessages(prev => [...prev, userMessage]);

    // Create a placeholder for the agent message
    const agentMessageId = Date.now();
    setMessages(prev => [...prev, {
        id: agentMessageId,
        role: 'agent',
        content: '',
        thinking: [],
        timestamp: new Date().toLocaleTimeString(),
        loading: true
    }]);

    setLoading(true);
    setInput('');

    // Create new AbortController
    abortControllerRef.current = new AbortController();

    try {
      const baseURL = client.defaults.baseURL || '';

      // Route to CAD-specific agent endpoint when CAD mode is active
      const endpoint = cadMode ? `${baseURL}/api/v1/cad-agent/invoke` : `${baseURL}/api/v1/invoke`;

      const requestBody = cadMode
        ? {
            input_text: currentInput,
            session_id: sessionId,
            drawing_id: context?.drawing_id || null,
            attached_file_ids: currentSelectedFiles,
            context: context,
            mcp_mode: false,
          }
        : {
            input_text: currentInput,
            session_id: sessionId,
            enable_trace: false,
            attached_file_ids: currentSelectedFiles,
            context: context
          };

      // Get auth token for the request
      const token = await getFreshToken();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        signal: abortControllerRef.current.signal,
        body: JSON.stringify(requestBody)
      });

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('CAD Agent error:', response.status, errorText);
        throw new Error(`Request failed: ${response.status} - ${errorText}`);
      }

      // Stream NDJSON: read chunks, split on newlines, parse each JSON line.
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let parseErrorCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
            if (!line.trim()) continue;
            try {
                const data = JSON.parse(line);

                if (data.type === 'keepalive') continue;

                setMessages(prev => prev.map(msg => {
                    if (msg.id !== agentMessageId) return msg;

                    if (data.type === 'step') {
                        return { ...msg, thinking: [...(msg.thinking || []), data.content] };
                    } else if (data.type === 'chunk') {
                        return { ...msg, content: msg.content + data.content };
                    } else if (data.type === 'navigate') {
                        window.dispatchEvent(new CustomEvent('epilotNavigate', {
                          detail: { section: data.section, tab: data.tab, entityId: data.entity_id, entityType: data.entity_type }
                        }));
                        return msg;
                    } else if (data.type === 'tool_call') {
                        return {
                          ...msg,
                          toolCalls: [...(msg.toolCalls || []), { name: data.name, input: data.input }]
                        };
                    } else if (data.type === 'drawing_created') {
                        if (data.drawing_id) {
                          window.dispatchEvent(new CustomEvent('cadDrawingCreated', {
                            detail: { drawingId: data.drawing_id, urn: data.urn }
                          }));
                          showSuccess('Drawing generated successfully');
                        }
                        return {
                          ...msg,
                          drawingCreated: {
                            drawing_id: data.drawing_id,
                            urn: data.urn
                          }
                        };
                    } else if (data.type === 'bom_extracted') {
                        return {
                          ...msg,
                          bomExtracted: {
                            drawing_id: data.drawing_id,
                            drawing_name: data.drawing_name,
                            extraction_type: data.extraction_type,
                            summary: data.summary,
                            layers: data.layers,
                            bom_items: data.bom_items,
                            texts: data.texts
                          }
                        };
                    } else if (data.type === 'job_started') {
                        return { ...msg, jobId: data.job_id };
                    } else if (data.type === 'job_complete') {
                        if (data.drawing_id && data.urn) {
                          window.dispatchEvent(new CustomEvent('cadDrawingCreated', {
                            detail: { drawingId: data.drawing_id, urn: data.urn }
                          }));
                        }
                        return msg;
                    } else if (data.type === 'model_created') {
                        return {
                          ...msg,
                          modelCreated: {
                            job_id: data.job_id,
                            download_url: data.download_url,
                            output_format: data.output_format,
                          },
                        };
                    } else if (data.type === 'final') {
                        window.dispatchEvent(new CustomEvent('onboarding:complete', { detail: 'ask_epiot' }));
                        return {
                            ...msg,
                            content: data.response || msg.content,
                            citations: data.citations,
                            loading: false
                        };
                    } else if (data.type === 'error') {
                        return { ...msg, content: sanitizeError(data.content), loading: false, error: true };
                    }
                    return msg;
                }));
            } catch (e) {
                parseErrorCount++;
                console.error(`Error parsing stream (#${parseErrorCount}):`, e);
                if (parseErrorCount >= 10) {
                    console.warn('Chat stream: too many parse errors, aborting');
                    break;
                }
            }
        }
      }
      // Safety: if stream ended without a 'final' event, clear loading state
      setMessages(prev => prev.map(msg => {
        if (msg.id !== agentMessageId || !msg.loading) return msg;
        return { ...msg, loading: false };
      }));
    } catch (error) {
      if (error.name === 'AbortError') {
          setMessages(prev => prev.map(msg => {
              if (msg.id !== agentMessageId) return msg;
              return { ...msg, content: msg.content + ' [Stopped by user]', loading: false };
          }));
          return;
      }
      setMessages(prev => prev.map(msg => {
          if (msg.id !== agentMessageId) return msg;
          // If a drawing or 3D model was already created, the generation succeeded
          // even if the stream closed before the final summary arrived.
          if (msg.drawingCreated || msg.modelCreated) {
              return { ...msg, content: msg.content || 'Generation complete.', loading: false };
          }
          showErrorNotif('AI request failed. Please try again.');
          return { ...msg, content: 'Sorry, I encountered an error. Please try again.', error: true, loading: false };
      }));
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Different suggested questions based on mode
  const suggestedQuestions = orchestratorMode
    ? [
        "Generate an execution plan for my pipeline",
        "What tools should I connect for stress analysis?",
        "Explain the data flow between AutoCAD and downstream tools",
        "What's the best way to track requirements across releases?"
      ]
    : cadMode
    ? [
        "Draw a steel baseplate 500x300mm with 4 bolt holes",
        "Create a 3D flange model with 6 mounting holes",
        "Modify the current drawing: add a 100mm hole at center",
        "Generate a railway track cross-section to UIC60 standard"
      ]
    : [
        "Summarize the attached document",
        "Generate a design review report for this project",
        "What are the key requirements in the uploaded spec?",
        "Compare the two attached files and list differences"
      ];

  return (
    <Box
      ref={chatContainerRef}
      sx={{
        background: isDark
          ? (cadMode
              ? 'linear-gradient(180deg, #1a1625 0%, #0f0d14 100%)'
              : 'linear-gradient(180deg, #1e1e1e 0%, #121212 100%)')
          : (cadMode
              ? 'linear-gradient(180deg, #f5f3ff 0%, #ede9fe 100%)'
              : 'linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%)'),
        borderRadius: 3,
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'background 0.3s ease',
      }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mb={1}
        pb={1}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', position: 'relative', flexShrink: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {orchestratorMode ? (
            <Hub sx={{
              fontSize: '1.15rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }} />
          ) : cadMode ? (
            <Architecture sx={{
              fontSize: '1.15rem',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }} />
          ) : (
            <AutoAwesome sx={{
              fontSize: '1.15rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }} />
          )}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: '1.15rem',
              letterSpacing: '-0.3px',
              background: cadMode
                ? 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%)'
                : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            e-pilot
          </Typography>
          {orchestratorMode && (
            <Typography sx={{ fontSize: '0.6rem', color: '#667eea', fontWeight: 600, ml: 0.5, bgcolor: 'rgba(102,126,234,0.1)', px: 0.8, py: 0.2, borderRadius: 1 }}>
              ORCHESTRATOR
            </Typography>
          )}
        </Box>

        {/* Mode Toggle and Refresh */}
        <Box sx={{ position: 'absolute', right: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!orchestratorMode && (<>
          {(activeSection === 'cad' || activeSection === 'cadviewer') && (
          <Tooltip title={cadMode ? "Switch to General Mode" : "Switch to CAD Mode"}>
            <Box data-tour="chat-cad-toggle" sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: cadMode ? '#7c3aed' : 'text.secondary',
                  fontWeight: cadMode ? 600 : 400,
                  mr: 0.5,
                }}
              >
                CAD
              </Typography>
              <Switch
                size="small"
                checked={cadMode}
                onChange={(e) => handleCadModeToggle(e.target.checked)}
                sx={{
                  width: 36,
                  height: 20,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: 0,
                    margin: '2px',
                    transitionDuration: '200ms',
                    '&.Mui-checked': {
                      transform: 'translateX(16px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#8b5cf6',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 16,
                    height: 16,
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 10,
                    backgroundColor: '#e0e0e0',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Tooltip>
          )}
          {/* MCP toggle hidden — MCP pipeline kept in code but not user-facing yet */}
          {false && cadMode && (
          <Tooltip title={mcpMode ? "Switch to EzDXF engine" : "Switch to AutoCAD MCP engine"}>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: mcpMode ? '#0d9488' : 'text.secondary',
                  fontWeight: mcpMode ? 600 : 400,
                  mr: 0.5,
                }}
              >
                MCP
              </Typography>
              <Switch
                size="small"
                checked={mcpMode}
                onChange={(e) => setMcpMode(e.target.checked)}
                sx={{
                  width: 36,
                  height: 20,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: 0,
                    margin: '2px',
                    transitionDuration: '200ms',
                    '&.Mui-checked': {
                      transform: 'translateX(16px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#0d9488',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 16,
                    height: 16,
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 10,
                    backgroundColor: '#e0e0e0',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Tooltip>
          )}
          </>)}
          <Tooltip title="Refresh File List">
            <IconButton
              size="small"
              onClick={fetchFiles}
              sx={{
                width: 28,
                height: 28,
                bgcolor: cadMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(102, 126, 234, 0.1)',
                '&:hover': {
                  bgcolor: cadMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(102, 126, 234, 0.2)',
                }
              }}
            >
              <Refresh sx={{ fontSize: 16, color: cadMode ? '#8b5cf6' : '#667eea' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Semantic Search Bar */}
      <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <Search sx={{ mr: 0.5, fontSize: 18, color: '#667eea', opacity: 0.6 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  fontSize: '0.8rem',
                  '& input': {
                    py: 0.75,
                  },
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching}
              size="small"
              sx={{
                minWidth: 70,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                py: 0.75,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                },
              }}
            >
              {isSearching ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Search'}
            </Button>
          </Box>

        </Box>

      {/* File Attachment - shown in both normal and CAD mode */}
      <FileAttachmentList
        allFiles={allFiles}
        selectedFiles={selectedFiles}
        toggleFileSelection={toggleFileSelection}
        displayedFiles={displayedFiles}
        searchQuery={searchQuery}
      />

      <MessageList
        messages={messages}
        allFiles={allFiles}
        setInput={setInput}
        suggestedQuestions={suggestedQuestions}
        cadMode={cadMode}
      />

      <Box data-tour="chat-input" data-onboarding="ask_epiot">
      <MessageInput
        input={input}
        setInput={setInput}
        sendMessage={sendMessage}
        loading={loading}
        handleStop={handleStop}
        onClearChat={handleClearChat}
        hasMessages={messages.length > 0}
        cadMode={cadMode}
      />
      </Box>
    </Box>
  );
}

export default ChatContainer;
